/**
 * Уведомления antd.
 *
 * Экземпляр берётся из `App.useApp()` через `bindNotification`, чтобы
 * тема и локаль совпадали с остальным интерфейсом. Пока мост не подключён,
 * используется статический `notification`.
 *
 * Подсказка ошибки уходит в `description`: у уведомления есть отдельное
 * поле, и причину отказа 1С терять нельзя.
 */

import { notification as staticNotification, Spin } from "antd";
import type { NotificationInstance } from "antd/es/notification/interface";
import { createElement } from "react";

import { describeError, isAbort } from "./client";

const DURATION = {
  info: 3,
  success: 3,
  warning: 5,
  error: 6,
} as const;

type Kind = keyof typeof DURATION;

let holder: NotificationInstance | null = null;

export function bindNotification(instance: NotificationInstance): void {
  holder = instance;
}

function api(): NotificationInstance {
  return holder ?? staticNotification;
}

function show(kind: Kind, text: string, hint?: string | null): void {
  api()[kind]({
    title: text,
    description: hint ?? undefined,
    duration: DURATION[kind],
  });
}

export const notify = {
  info: (text: string, hint?: string) => show("info", text, hint),
  success: (text: string, hint?: string) => show("success", text, hint),
  warning: (text: string, hint?: string) => show("warning", text, hint),
  error: (text: string, hint?: string) => show("error", text, hint),
  loading: (text: string) =>
    api().open({
      key: "sl1ce-loading",
      title: text,
      duration: 0,
      icon: createElement(Spin, { size: "small" }),
    }),
  clear: () => api().destroy(),
  failure(cause: unknown, prefix?: string): void {
    if (isAbort(cause)) return;
    const { detail, hint } = describeError(cause);
    show("error", prefix ? `${prefix}: ${detail}` : detail, hint);
  },
};
